import { createSelector } from 'reselect';
import { compose, mapValues, sortBy, lowerCase, get, find, reject } from 'lodash/fp';
import { getFullName, mewsEnterprise, getImageUrlOrNull } from '@mews-commander/utils';
import { getCurrentEnterpriseId, getCurrentUser } from '@mews-commander/core';
import { getPlatformScopeProfileId, getScopes } from '@mews-commander/legacy-entities/src/ducks/scopes';
import { getEnterprises } from '@mews-commander/legacy-entities/src/ducks/enterprises';
import { hasCurrentEnterpriseServiceProviderMode } from '@mews-commander/legacy-entities/src/selectors/session/subscription';
import {
    isCurrentProfilePlatformAdmin,
    isCurrentProfileEnabledToManageCustomers,
    hasReadOnlyPrivilegesForCustomerData,
    hasReadOnlyPrivilegesForCompanyData,
} from '@mews-commander/legacy-entities/src/selectors/session/scope';
import { getSubscriptions } from '@mews-commander/legacy-entities/src/ducks/subscriptions';
import { getImageBaseUrl } from '../app/reducers/configuration';

// @ts-expect-error $TSFixMeError: Parameter 'obj' implicitly has an 'any' type.
const getImageId = (obj, imageProperty = 'imageId') => ({
    imageId: get(imageProperty, obj),
});

const getAllUserMenuAccounts = createSelector(
    getScopes,
    getEnterprises,
    getImageBaseUrl,
    () => getCurrentUser(),
    getPlatformScopeProfileId,
    getSubscriptions,
    (scopes, enterprises, imageBaseUrl, user, platformProfileId, subscriptions) => {
        const getSaveImageUrl = getImageUrlOrNull(imageBaseUrl);

        const getCommonUserProperties = ({ enterprise }: $TSFixMe) => ({
            userId: user.id,
            name: enterprise.name,
            shortName: enterprise.shortName,
            fullName: getFullName(user),
            enterpriseAltText: user.lastName || enterprise.name,
            accountAltText: enterprise.name,
            displayName: enterprise.name || enterprise.shortName,
            loginScreenImage: getSaveImageUrl(getImageId(enterprise, 'coverImageId')),
            accountName: enterprise.name,
            accountImageUrl: getSaveImageUrl(getImageId(user)),
            enterpriseImageUrl: getSaveImageUrl(getImageId(enterprise, 'logoImageId')),
        });

        let allEnterprises = { ...enterprises };

        if (platformProfileId) {
            allEnterprises = {
                // @ts-expect-error $TSFixMeError: Type '{ [x: string]: ExtendedEnterprise | { profileId: any; coverImageId?: string | null | undefined; logoImageId?: stri...
                [mewsEnterprise.mewsId]: { ...mewsEnterprise, profileId: platformProfileId },
                ...allEnterprises,
            };
        }

        const enterpriseMapper = (enterprise: $TSFixMe) => {
            const { id: enterpriseId, subscriptionId } = enterprise;
            const scope = find({ enterpriseId }, scopes);
            const subscription = subscriptions[subscriptionId];
            const profileId = scope ? (scope as $TSFixMe).profileId : platformProfileId;

            return {
                ...getCommonUserProperties({ enterprise }),
                profileId,
                enterpriseId,
                subscriptionCode: subscription?.code,
                isPortfolio: Boolean(subscription?.operationModes.isPortfolio),
            };
        };

        return compose(
            sortBy(({ displayName }) => lowerCase(displayName)),
            mapValues(enterpriseMapper)
        )(allEnterprises);
    }
);

export const getCurrentUserMenuAccount = createSelector(
    getCurrentEnterpriseId,
    getAllUserMenuAccounts,
    (enterpriseId, accounts) => find({ enterpriseId }, accounts)
);

export const getOtherUserMenuAccounts = createSelector(
    getCurrentEnterpriseId,
    getAllUserMenuAccounts,
    (enterpriseId, accounts) => reject({ enterpriseId }, accounts)
);

export const canCurrentEmployeeUseSearchbar = createSelector(
    isCurrentProfilePlatformAdmin,
    isCurrentProfileEnabledToManageCustomers,
    hasCurrentEnterpriseServiceProviderMode,
    hasReadOnlyPrivilegesForCompanyData,
    hasReadOnlyPrivilegesForCustomerData,
    (
        isPlatformAdmin,
        canManageCustomers,
        hasServiceProviderMode,
        readOnlyPrivilegesForCustomers,
        readOnlyPrivilegesForCompanies
    ) => {
        if (isPlatformAdmin) return false;

        return (
            (hasServiceProviderMode && canManageCustomers) ||
            readOnlyPrivilegesForCustomers ||
            readOnlyPrivilegesForCompanies
        );
    }
);
